import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { animated, useSpring } from '@react-spring/web'
import QRCode from 'qrcode'

import { useViewStore } from '../Contexts/ViewContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useLocaleStore } from '../Contexts/LocaleContext'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useUtilsStore } from '../Contexts/UtilsContext'

import useRefDimensions from '../../hooks/useRefDimensions'
import { Mode, UIPart } from '../../models/enums'

import QRCodeComponent from '../Utils/QRCodeComponent'
import CloseButton from '../Common/CloseButton'
import { RoundIcon } from '../Icons/DetailedPOIIcons'

interface GridProps {
  title?: string
  history?: boolean
  qrCodeDataUrl?: string
  setQrCodeDataUrl?: (url: string | undefined) => void
  children: ReactNode
}

const springQrCodeDuration = 500

const Grid = ({ title, history, qrCodeDataUrl, setQrCodeDataUrl, children }: GridProps): JSX.Element => {
  const isLandscape = useViewStore((state) => state.isLandscape)
  const mainWidth = useViewStore((state) => state.currentWidth)
  const mainHeight = useViewStore((state) => state.currentHeight)
  const mode = useSpeechStore((state) => state.mode)
  const configuration = useRetorikStore((state) => state.configuration)
  const translation = useLocaleStore((state) => state.currentTranslations)
  const isRTL = useLocaleStore((state) => state.isRTL)
  const clickedOnUI = useUtilsStore((state) => state.clickedOnUI)
  const setClickedOnUI = useUtilsStore((state) => state.actions.setClickedOnUI)

  const useOldRemote = useRetorikStore((state) => state.configuration.useOldRemote)

  const [classname, setClassname] = useState<string>('')
  const [qrCodeData, setQrCodeData] = useState<string | undefined>(undefined)
  const fadeInTimerRef = useRef<NodeJS.Timer | null>(null)
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const gridContainerDimensions = useRefDimensions(gridContainerRef)

  const nbCards = useMemo<number>(() => {
    return React.Children.count(children)
  }, [children])

  const rowGap = useMemo<number>(() => {
    return gridContainerDimensions.width * 0.035
  }, [gridContainerDimensions.width])

  const maxHeight = useMemo<number>(() => {
    // 0.58 quite equals 7/12. This ratio prevents the container from overflowing vertically
    return mainHeight * 0.58
  }, [mainHeight])

  const newMaxHeight = useMemo<number>(() => {
    if (mode === Mode.vocal) {
      // Base positions of container on the grid are 3 -> 11 for landscape and 6 -> 11 for borne
      let start = isLandscape ? 3 : 6
      let end = 11

      if (typeof configuration.answerpanel === 'object') {
        start = isLandscape ? configuration.answerpanel.landscape?.containerRowStart || 3 : configuration.answerpanel.borne?.containerRowStart || 6
        end = isLandscape ? configuration.answerpanel.landscape?.containerRowEnd || 11 : configuration.answerpanel.borne?.containerRowEnd || 11
      }

      return (0.75 * mainHeight * (end - start)) / 12
    } else {
      // Base positions of container on the grid are 1 -> 11 for landscape and 6 -> 13 for borne
      let start = isLandscape ? 1 : 6
      let end = isLandscape ? 11 : 13

      if (typeof configuration.answerpanel === 'object') {
        start = isLandscape ? configuration.answerpanel.landscape?.containerRowStartText || 1 : configuration.answerpanel.borne?.containerRowStart || 6
        end = isLandscape ? configuration.answerpanel.landscape?.containerRowEndText || 11 : configuration.answerpanel.borne?.containerRowEnd || 13
      }

      return (0.75 * mainHeight * (end - start)) / 12
    }
  }, [mainHeight, isLandscape, mode, configuration.answerpanel])

  const [qrCodeSpring, qrCodeApi] = useSpring(() => ({
    from: {
      opacity: 0
    }
  }))

  useEffect(() => {
    qrCodeData &&
      qrCodeApi.start({
        from: {
          opacity: 0
        },
        to: {
          opacity: 1
        },
        config: {
          duration: springQrCodeDuration
        }
      })

    return () => {
      fadeInTimerRef?.current && clearTimeout(fadeInTimerRef.current)
    }
  }, [qrCodeData])

  const handleCloseQrCode = (): void => {
    qrCodeApi.start({
      from: {
        opacity: 1
      },
      to: {
        opacity: 0
      },
      config: {
        duration: springQrCodeDuration
      }
    })

    fadeInTimerRef?.current && clearTimeout(fadeInTimerRef.current)
    fadeInTimerRef.current = setTimeout(() => {
      setQrCodeDataUrl?.(undefined)
    }, springQrCodeDuration)
  }

  const setQrCodeDataAsync = async (url: string): Promise<void> => {
    const qrCodeString = await QRCode.toDataURL(url)
    setQrCodeData(qrCodeString)
  }

  useEffect(() => {
    qrCodeDataUrl ? setQrCodeDataAsync(qrCodeDataUrl) : setQrCodeData(undefined)
  }, [qrCodeDataUrl])

  useEffect(() => {
    // Close qrcode overlay if we click on another part of the UI
    if (clickedOnUI !== UIPart.none && clickedOnUI !== UIPart.card && clickedOnUI !== UIPart.qroverlay && qrCodeData) {
      handleCloseQrCode()
    }
  }, [clickedOnUI, qrCodeData])

  useEffect(() => {
    switch (nbCards) {
      case 1:
      case 2:
        setClassname('rf-grid-cols-[repeat(2,30%)] rf-grid-rows-1')
        break
      case 3:
        setClassname(mainHeight > mainWidth || (mainHeight < mainWidth && mainWidth > 1200) ? 'rf-grid-cols-[repeat(3,31%)] rf-grid-rows-1' : 'rf-grid-cols-[repeat(2,45%)] rf-grid-rows-2')
        break
      case 4:
        setClassname('rf-grid-cols-[repeat(2,30%)] rf-grid-rows-2')
        break
      case 5:
      case 6:
        setClassname(mainHeight > mainWidth || (mainHeight < mainWidth && mainWidth > 1200) ? 'rf-grid-cols-[repeat(3,31%)] rf-grid-rows-2' : 'rf-grid-cols-[repeat(2,45%)] rf-grid-rows-3')
        break
      case 7:
      case 8:
        setClassname(mainHeight > mainWidth || (mainHeight < mainWidth && mainWidth > 1200) ? 'rf-grid-cols-[repeat(3,31%)] rf-grid-rows-3' : 'rf-grid-cols-[repeat(2,45%)] rf-grid-rows-4')
        break
      case 9:
        setClassname(mainHeight > mainWidth || (mainHeight < mainWidth && mainWidth > 1200) ? 'rf-grid-cols-[repeat(3,31%)] rf-grid-rows-3' : 'rf-grid-cols-[repeat(2,45%)] rf-grid-rows-5')
        break
      default:
        break
    }
  }, [nbCards, mainWidth, mainHeight])

  const handleClick = (e: React.MouseEvent): void => {
    setClickedOnUI(UIPart.qroverlay)
    e.stopPropagation()
    e.preventDefault()
  }

  return nbCards ? (
    <div
      id='retorik-grid'
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`rf-w-full rf-flex rf-flex-col rf-gap-4 vertical:rf-w-9/10 ${
        history ? 'large-vertical:rf-w-full' : 'landscape-retorik:rf-pr-40 landscape-retorik:rf-pl-1/5 large-vertical:rf-w-3/5'
      } vertical:rf-items-center`}
    >
      {title && (
        <div
          className={`rf-w-full ${isRTL ? 'rf-text-right' : 'rf-text-left'} rf-title-large-size-auto ${history ? 'rf-text-textModePanelConversationBot' : 'rf-text-truewhite rf-text-shadow-black'}`}
        >
          {title}
        </div>
      )}
      <div
        ref={gridContainerRef}
        className={`rf-relative rf-w-full rf-grid rf-justify-center rf-gap-x-[3.5%] ${classname}`}
        style={{
          maxHeight: history || mainHeight > mainWidth ? 'none' : useOldRemote ? maxHeight : newMaxHeight,
          rowGap: rowGap
        }}
      >
        {children}

        {/* Qrcode as overlay in borne mode */}
        {qrCodeData && (
          <animated.div
            className='rf-absolute rf-h-full rf-w-full rf-top-0 rf-left-0 rf-z-ui rf-flex rf-flex-col rf-justify-center rf-items-center rf-gap-2 rf-p-2 rf-text-truewhite'
            style={{
              background: 'rgba(18,18,18,0.7)',
              ...qrCodeSpring
            }}
            onClick={(e) => handleClick(e)}
          >
            <CloseButton onClick={handleCloseQrCode} showLabel={false} />

            {/* Icon + Text */}
            <div className='rf-px-8 rf-pb-4 rf-flex rf-flex-row rf-items-center rf-gap-1 rf-italic'>
              <RoundIcon color='#FFF' />
              <div className='rf-w-fit rf-text-medium-size-auto'>{translation.link.scanQRTile}</div>
            </div>

            {/* QR code */}
            <QRCodeComponent src={qrCodeData} largeMaxHeight={true} />
          </animated.div>
        )}
      </div>
    </div>
  ) : (
    <React.Fragment />
  )
}

export default Grid
